import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, apiDownload, describeError } from '../api';
import { FileButton, JsonEditor, Json, Message, Pill, Section, fmtTime, useJsonText } from '../components/ui';
import { useSession } from '../session';

const DOMAINS = ['ui_prefs', 'power_user', 'api_settings', 'extension_settings', 'tags', 'personas', 'wi_globals'] as const;
interface Doc { domain: string; version: number; json: Record<string, unknown>; updated_at: string }

export function SettingsPage() {
  const { env, me } = useSession();
  const [docs, setDocs] = useState<Record<string, Doc>>({});
  const [domain, setDomain] = useState<(typeof DOMAINS)[number]>('ui_prefs');
  const [version, setVersion] = useState<number | null>(null);
  const full = useJsonText({});
  const patch = useJsonText({ example_key: 'value', remove_me: null });
  const [conflict, setConflict] = useState<{ version: number; json: unknown } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!me) return;
    try {
      const { data } = await api<Record<string, Doc>>('/api/me/settings');
      setDocs(data);
    } catch (e) { setErr(describeError(e)); }
  }, [me]);
  useEffect(() => { void load(); }, [load, env]);
  useEffect(() => {
    const d = docs[domain];
    if (d && version === null) { full.reset(d.json); setVersion(d.version); }
  }, [docs, domain, version, full]);

  const pick = (d: (typeof DOMAINS)[number]) => { setDomain(d); setVersion(null); setConflict(null); setMsg(null); setErr(null); };
  const handle = async (fn: () => Promise<string>) => {
    setErr(null); setMsg(null); setConflict(null);
    try { setMsg(await fn()); await load(); setVersion(null); } catch (e) {
      if (e instanceof ApiFailure && e.error.code === 'LIBRARY_VERSION_CONFLICT') setConflict({ version: e.error.version as number, json: e.error.json });
      setErr(describeError(e));
    }
  };
  const put = () => { const v = full.parse(); if (v === undefined) return; void handle(async () => { const { data } = await api<Doc>(`/api/me/settings/${domain}`, { method: 'PUT', body: v, headers: { 'If-Match': `"${version}"` } }); return `PUT 成功 → version ${data.version}`; }); };
  const mergePatch = () => { const v = patch.parse(); if (v === undefined) return; void handle(async () => { const { data } = await api<Doc>(`/api/me/settings/${domain}`, { method: 'PATCH', body: { version, patch: v } }); return `PATCH 成功 → version ${data.version}`; }); };
  const importSt = (file: File) => void handle(async () => { const settings = JSON.parse(await file.text()) as unknown; await api('/api/me/settings/import-st', { method: 'POST', body: { settings } }); return '已导入 settings.json（七域整体覆盖）'; });

  const current = docs[domain];
  return (
    <div className="space-y-4">
      <Section
        title="设置文档（七域，版本化）"
        actions={
          <>
            <FileButton label="导入 ST settings.json" accept=".json" onFile={importSt} disabled={!me} />
            <button className="btn-ghost" disabled={!me} onClick={() => void apiDownload('/api/me/settings/export-st', 'settings.json').catch((e) => setErr(describeError(e)))}>导出 settings.json</button>
          </>
        }
      >
        {!me && <Message text="请先登录" />}
        <div className="flex flex-wrap gap-1">
          {DOMAINS.map((d) => <button key={d} onClick={() => pick(d)} className={`rounded-lg px-2 py-1 text-xs ${domain === d ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-zinc-100'}`}>{d} {docs[d] && <Pill>v{docs[d]!.version}</Pill>}</button>)}
        </div>
        {current && <div className="text-xs text-zinc-400">当前 version <span className="font-mono">{current.version}</span> · 更新 {fmtTime(current.updated_at)} · 本地基线 version <span className="font-mono">{version}</span>（两个标签页同时改同一域即可复现 409）</div>}
        <Message text={err} kind="error" />
        <Message text={msg} kind="ok" />
        {conflict && (
          <div className="space-y-2 rounded-lg border border-amber-900/60 bg-amber-950/20 p-3">
            <div className="text-xs text-amber-200">服务端 version {conflict.version} 与本地基线 {version} 不一致。下面是服务端当前 json：</div>
            <Json value={conflict.json} className="max-h-48" />
            <button className="btn-ghost" onClick={() => { full.reset(conflict.json); setVersion(conflict.version); setConflict(null); }}>采用服务端版本作为新基线</button>
          </div>
        )}
      </Section>
      <div className="grid gap-4 lg:grid-cols-2">
        <Section title={`PUT 整体替换 · ${domain}`} actions={<button className="btn" disabled={!current} onClick={put}>PUT（If-Match: "{version}"）</button>}>
          <JsonEditor value={full.text} onChange={full.setText} error={full.error} rows={22} />
        </Section>
        <Section title="PATCH merge-patch（RFC 7396：null 删键、数组整体替换）" actions={<button className="btn" disabled={!current} onClick={mergePatch}>PATCH（body.version = {version}）</button>}>
          <JsonEditor value={patch.text} onChange={patch.setText} error={patch.error} rows={10} />
          <button className="btn-ghost" disabled={!current} onClick={() => void handle(async () => { await api(`/api/me/settings/${domain}`, { method: 'PATCH', body: { x: 1 } }); return '???'; })}>演示：不带版本 → 428</button>
        </Section>
      </div>
    </div>
  );
}
